import { isArray } from './is-array.js';

/**
 * @description
 * Deep comparison of two variables a and b, returning whether they are equal according to a recursive equality algorithm.
 *
 * @example
 * ``` ts
 * isDeepEqual({ a: [2, 3], b: [4] }, { a: [2, 3], b: [4] });
 * // => true
 * isDeepEqual([null, null, null], [null, null, null]);
 * // => true
 * isDeepEqual([{ a: 3 }, { b: 4 }], [{ a: '3' }, { b: '4' }]);
 * // => false
 * isDeepEqual(NaN, 'abc');
 * // => false
 * ```
 *
 * @param a The a to check.
 * @param b The b to check.
 * @returns Returns true if value is a equal, else false.
 *
 * @docsCategory preference/lang
 * @codeFilter is-deep-equal
 */
export function isDeepEqual(a, b): boolean {
  if (a === b) return true;
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const arrA = isArray(a);
    const arrB = isArray(b);
    let i;
    let length;
    let key;

    if (arrA && arrB) {
      length = a.length;
      if (length !== b.length) {
        return false;
      }
      for (i = length; i-- !== 0; ) {
        if (!isDeepEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }

    if (arrA !== arrB) {
      return false;
    }

    const dateA = a instanceof Date;
    const dateB = b instanceof Date;
    if (dateA !== dateB) return false;
    if (dateA && dateB) return a.getTime() === b.getTime();

    const regexpA = a instanceof RegExp;
    const regexpB = b instanceof RegExp;
    if (regexpA !== regexpB) return false;
    if (regexpA && regexpB) return a.toString() === b.toString();

    const keys = Object.keys(a);
    length = keys.length;

    if (length !== Object.keys(b).length) {
      return false;
    }
    for (i = length; i-- !== 0; ) {
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) {
        return false;
      }
    }
    for (i = length; i-- !== 0; ) {
      key = keys[i];
      if (!isDeepEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }
  return a !== a && b !== b;
}
